import { useState } from "react";
import {
	Alert,
	Button,
	DatePicker,
	Form,
	Input,
	Select,
	Space,
	Modal,
	Typography,
	Image,
} from "antd";
import {
	MinusCircleOutlined,
	PlusOutlined,
	CheckCircleFilled,
	ExclamationCircleFilled,
	LoadingOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { getRoles } from "../../role/roleApis";
import BtnEditSvg from "../Button/btnEditSvg";
import dayjs from "dayjs";
import {
	clearUser,
	loadSingleStaff,
	updateUser,
} from "../../../redux/rtk/features/user/userSlice";
import { toast } from "react-toastify";
import { getDepartments } from "../../department/departmentApis";
import { loadAllShift } from "../../../redux/rtk/features/shift/shiftSlice";
import { loadAllLeavePolicy } from "../../../redux/rtk/features/leavePolicy/leavePolicySlice";
import { loadAllWeeklyHoliday } from "../../../redux/rtk/features/weeklyHoliday/weeklyHolidaySlice";
import { useParams } from "react-router-dom";
import baseUrl from "../../../utils/baseUrl";
import { getStripeStatus, connectStripeAccount } from "../../stripe/stripeApis";

const ProfileEditPopup = ({ data }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [loader, setLoader] = useState(false);
	const [newImage, setNewImage] = useState(null);

	const leavePolicy = useSelector((state) => state.leavePolicy?.list);
	const weeklyHoliday = useSelector((state) => state.weeklyHoliday?.list);
	const shift = useSelector((state) => state.shift?.list);
	const user = useSelector((state) => state.users?.user);

	const { id } = useParams("id");

	const dispatch = useDispatch();
	const { Title } = Typography;
	const { Option } = Select;
	const [list, setList] = useState(null);

	const [department, setDepartment] = useState(null);

	const [initialValues, setInitialValues] = useState({});

	const [roleId, setRoleId] = useState("");
	const [departmentId, setDepartmentId] = useState("");
	const [shiftId, setShiftId] = useState("");
	const [leavePolicyId, setLeavePolicyId] = useState("");
	const [weeklyHolidayId, setWeeklyHolidayId] = useState("");

	const [stripeStatus, setStripeStatus] = useState(null);
	const [stripeConnecting, setStripeConnecting] = useState(false);
	const [stripeInviteModalOpen, setStripeInviteModalOpen] = useState(false);
	const [stripeInviteLink, setStripeInviteLink] = useState("");

	useEffect(() => {
		getRoles()
			.then((d) => setList(d))
			.catch((error) => console.log(error));
	}, []);

	useEffect(() => {
		dispatch(loadAllShift());
		dispatch(loadAllLeavePolicy());
		dispatch(loadAllWeeklyHoliday());
	}, []);

	useEffect(() => {
		dispatch(loadSingleStaff(id));
		setInitialValues({
			firstName: user.firstName ? user.firstName : "",
			lastName: user.lastName ? user.lastName : "",
			userName: user.userName ? user.userName : "",
			email: user.email ? user.email : "",
			hourlyRate: user.hourlyRate ? user.hourlyRate : "",
			stripeAccountId: user.stripeAccountId ? user.stripeAccountId : "",
			phone: user.phone ? user.phone : "",
			street: user.street ? user.street : "",
			city: user.city ? user.city : "",
			state: user.state ? user.state : "",
			zipCode: user.zipCode ? user.zipCode : "",
			country: user.country ? user.country : "",
			// joinDate: dayjs(user.joinDate),
			leaveDate: user.leaveDate ? dayjs(user.leaveDate) : null,
			employeeId: user.employeeId ? user.employeeId : "",
			bloodGroup: user.bloodGroup ? user.bloodGroup : "",
			image: user.image ? user.image : "",
			roleId: user.roleId ? user.roleId : "",
			departmentId: user.departmentId ? user.departmentId : "",
			shiftId: user.shiftId ? user.shiftId : "",
			leavePolicyId: user.leavePolicyId ? user.leavePolicyId : "",
			weeklyHolidayId: user.weeklyHolidayId ? user.weeklyHolidayId : "",
		});
		setRoleId(user.roleId || "");
	}, [id]);

	useEffect(() => {
		getDepartments()
			.then((d) => setDepartment(d))
			.catch((error) => console.log(error));
	}, []);

	const refreshStripeStatus = () => {
		getStripeStatus(id)
			.then((d) => setStripeStatus(d))
			.catch((error) => console.log(error));
	};

	useEffect(() => {
		if (Number(roleId) === 3 && isModalOpen) {
			refreshStripeStatus();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [roleId, isModalOpen, id]);

	const handleStripeConnect = async () => {
		try {
			setStripeConnecting(true);
			const result = await connectStripeAccount(id);
			toast.success(
				result.emailStatus === "sent"
					? "Onboarding link emailed to the service man — you can also copy it below"
					: "Onboarding link created — copy it below to share manually"
			);
			setStripeInviteLink(result.onboardingUrl);
			setStripeInviteModalOpen(true);
			refreshStripeStatus();
		} catch (error) {
			toast.error(
				error?.response?.data?.message || "Failed to create onboarding link"
			);
		} finally {
			setStripeConnecting(false);
		}
	};

	const [form] = Form.useForm();

const onFinish = async (values) => {
	try {
		setLoader(true);

		// Convert hourlyRate to float if provided
		if (values.hourlyRate) {
			values.hourlyRate = parseFloat(values.hourlyRate);
		}

		// Create FormData for multipart/form-data
		const formData = new FormData();

		// Append all text fields
		formData.append("firstName", values.firstName || "");
		formData.append("lastName", values.lastName || "");
		formData.append("userName", values.userName || "");
		formData.append("email", values.email || "");
		formData.append("password", values.password || ""); // Allow blank to skip update
		formData.append("hourlyRate", values.hourlyRate || "");
		formData.append("phone", values.phone || "");
		formData.append("street", values.street || "");
		formData.append("city", values.city || "");
		formData.append("state", values.state || "");
		formData.append("zipCode", values.zipCode || "");
		formData.append("country", values.country || "");
		formData.append("employeeId", values.employeeId || "");
		formData.append("bloodGroup", values.bloodGroup || "");
		formData.append("stripeAccountId", values.stripeAccountId || "");
		// Handle optional date fields
		if (values.leaveDate) {
			formData.append("leaveDate", values.leaveDate.toISOString());
		}

		// Role and other relation fields
		formData.append("roleId", roleId ? roleId : data.roleId);
		formData.append("departmentId", departmentId ? departmentId : data.departmentId);
		formData.append("shiftId", shiftId ? shiftId : data.shiftId);
		formData.append("leavePolicyId", leavePolicyId ? leavePolicyId : data.leavePolicyId);
		formData.append("weeklyHolidayId", weeklyHolidayId ? weeklyHolidayId : data.weeklyHolidayId);

		// Append image if new image is selected
		if (newImage) {
			formData.append("image", newImage);
		}

		// Dispatch update with formData
		const resp = await dispatch(
			updateUser({
				id: id,
				values: formData, // must be handled as multipart/form-data in backend
			})
		);

		if (resp.payload.message === "success") {
			dispatch(clearUser());
			dispatch(loadSingleStaff(id));
			setIsModalOpen(false);
		} else {
			toast.error("Update failed");
		}

		setLoader(false);
	} catch (error) {
		console.error("Update error:", error.message);
		setLoader(false);
		toast.error("Something went wrong");
	}
};



	const onFinishFailed = (errorInfo) => {
		setLoader(false);
		console.log("Failed:", errorInfo);
	};

	const bloodGroups = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"]; // blood groups

	const showModal = () => {
		setIsModalOpen(true);
	};
	const handleOk = () => {
		setIsModalOpen(false);
	};
	const handleCancel = () => {
		setIsModalOpen(false);
	};

	return (
		<>
			<button onClick={showModal}>
				<BtnEditSvg size={30} />
			</button>
			<Modal
				width={"50%"}
				title='Update User Information'
				open={isModalOpen}
				onOk={handleOk}
				onCancel={handleCancel}>
				<Form
					size='small'
					form={form}
					name='basic'
					labelCol={{
						span: 5,
					}}
					wrapperCol={{
						span: 14,
					}}
					initialValues={initialValues}
					onFinish={onFinish}
					onFinishFailed={onFinishFailed}
					autoComplete='off'>
					<h2 className='text-center text-xl mt-3 mb-3 txt-color'>
						User Information
					</h2>
<div className='text-center mb-4'>
  {user?.image ? (
    <Image
      width={100}
      src={
        newImage
          ? URL.createObjectURL(newImage)
          : `${baseUrl}/files/${user.image}`
      }
      alt="User"
      style={{ borderRadius: "50%" }}
    />
  ) : (
    <h3 className="text-base font-medium txt-color-secondary">No Image</h3>
  )}

  <div style={{ marginTop: "10px" }}>
    <Form.Item label="Update Image" name="image" valuePropName="file">
  <input
  type="file"
  accept="image/*"
  onChange={(e) => {
    const file = e.target.files[0];
    if (file) {
      form.setFieldsValue({ image: file }); // optional
      setNewImage(file); // ✅ crucial for appending image to FormData
    }
  }}
/>
</Form.Item>
  </div>
</div>
					<Form.Item
						style={{ marginBottom: "10px" }}
						label='First Name'
						name='firstName'
						rules={[
							{
								required: true,
								message: "Please input First Name!",
							},
						]}>
						<Input placeholder='John' />
					</Form.Item>
					<Form.Item
						style={{ marginBottom: "10px" }}
						label='Last Name'
						name='lastName'
						rules={[
							{
								required: true,
								message: "Please input Last Name!",
							},
						]}>
						<Input placeholder='Doe' />
					</Form.Item>
					<Form.Item
						style={{ marginBottom: "10px" }}
						label='User Name'
						name='userName'
						rules={[
							{
								required: true,
								message: "Please input User Name!",
							},
						]}>
						<Input placeholder='john_doe' />
					</Form.Item>
					<Form.Item
						style={{ marginBottom: "10px" }}
						label='Password'
						name='password'
						tooltip='Leave blank to keep the current password'
						>
						<Input.Password placeholder='Enter new password (leave blank to keep existing)' />
					</Form.Item>
					<Form.Item
						style={{ marginBottom: "10px" }}
						label='Email'
						name='email'
						rules={[
							{
								required: true,
								message: "Please input email!",
							},
						]}>
						<Input placeholder='johndoe2@example.com' />
					</Form.Item>
					{Number(roleId) === 3 && (
  <Form.Item
    style={{ marginBottom: "10px" }}
    label='Hourly Rate(€)'
    name='hourlyRate'
    rules={[
      {
        required: true,
        message: "Please input hourly rate",
      },
    ]}>
    <Input placeholder='10' />
  </Form.Item>

  
)}

{Number(roleId) === 3 && (
  <Form.Item
    style={{ marginBottom: "10px" }}
    label='Stripe Id'
    name='stripeAccountId'
    rules={[
      {
        required: true,
        message: "Please input Stripe Account Id",
      },
    ]}>
    <Input placeholder="acct_xxxxxxxxxxxxx" />
  </Form.Item>


)}

{Number(roleId) === 3 && (
  <Form.Item
    style={{ marginBottom: "10px" }}
    label='Stripe Payment Setup'>
    <div
      style={{
        border: "1px solid #f0f0f0",
        borderRadius: 10,
        padding: 14,
        background: "#fafafa",
      }}>
      {stripeStatus === null && (
        <div style={{ color: "#8c8c8c", display: "flex", alignItems: "center", gap: 8 }}>
          <LoadingOutlined /> Loading status…
        </div>
      )}

      {stripeStatus && stripeStatus.connected === false && (
        <Alert
          type='warning'
          showIcon
          message='No Stripe account connected yet'
          description='Send an onboarding link to let this service man set up payouts.'
        />
      )}

      {stripeStatus && stripeStatus.connected && stripeStatus.transfersReady && (
        <Alert
          type='success'
          showIcon
          icon={<CheckCircleFilled />}
          message='Connected and ready to receive payments'
          description={
            <span style={{ fontFamily: "monospace", fontSize: 12 }}>
              {stripeStatus.accountId}
            </span>
          }
        />
      )}

      {stripeStatus && stripeStatus.connected && !stripeStatus.transfersReady && (
        <Alert
          type='error'
          showIcon
          icon={<ExclamationCircleFilled />}
          message='Onboarding incomplete'
          description={
            <>
              <div style={{ fontFamily: "monospace", fontSize: 12 }}>
                {stripeStatus.accountId}
              </div>
              {stripeStatus.requirementsDue?.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  Missing: {stripeStatus.requirementsDue.join(", ")}
                </div>
              )}
            </>
          }
        />
      )}

      <Button
        type='primary'
        block
        loading={stripeConnecting}
        onClick={handleStripeConnect}
        style={{ marginTop: 12 }}>
        {stripeStatus?.connected ? "Resend Onboarding Link" : "Send Onboarding Link"}
      </Button>
    </div>
  </Form.Item>
)}
{/* 
					<Form.Item
						style={{ marginBottom: "10px" }}
						label='Phone'
						name='phone'
						rules={[
							{
								required: true,
								message: "Please input phone!",
							},
						]}>
						<Input placeholder='1234584515' />
					</Form.Item> */}

					<h2 className='text-center text-xl mt-3 mb-3 txt-color'>
						Address Information
					</h2>
					<Form.Item
						style={{ marginBottom: "10px" }}
						label='Address'
						name='street'
						rules={[
							{
								required: true,
								message: "Please input street!",
							},
						]}>
						<Input placeholder='123 Main Street' style={{ width: "100%" }} />
					</Form.Item>
					<Form.Item
						style={{ marginBottom: "10px" }}
						label='City'
						name='city'
						rules={[{ required: true, message: "Please input city!" }]}>
						<Input placeholder='Los Angeles' />
					</Form.Item>
					{/* <Form.Item
						style={{ marginBottom: "10px" }}
						label='State'
						name='state'
						rules={[{ required: true, message: "Please input state!" }]}>
						<Input placeholder='CA' />
					</Form.Item> */}
					{/* <Form.Item
						style={{ marginBottom: "10px" }}
						label='Zip Code'
						name='zipCode'
						rules={[{ required: true, message: "Please input Zip Code!" }]}>
						<Input placeholder='90211' />
					</Form.Item> */}
					<Form.Item
						style={{ marginBottom: "10px" }}
						label='Country'
						name='country'
						rules={[{ required: true, message: "Please input Country!" }]}>
						<Input placeholder='USA' />
					</Form.Item>

					{/* <h2 className='text-center text-xl mt-3 mb-3 txt-color'>
						{" "}
						Employee Information{" "}
					</h2> */}
					{/* <Form.Item
						style={{ marginBottom: "10px" }}
						label='Joining Date'
						name='joinDate'
						valuePropName='date'
						rules={[
							{
								required: true,
								message: "Please input joining date!",
							},
						]}>
						<DatePicker
							className='date-picker hr-staffs-date-picker'
							defaultValue={initialValues.joinDate}
						/>
					</Form.Item> */}
					{/* <Form.Item
						style={{ marginBottom: "10px" }}
						label='Leave Date'
						name='leaveDate'
						valuePropName='leaveDate'>
						<DatePicker
							className='date-picker hr-staffs-date-picker'
							defaultValue={initialValues.leaveDate}
						/>
					</Form.Item>
					<Form.Item
						style={{ marginBottom: "10px" }}
						label='Employee ID'
						name='employeeId'
						rules={[
							{
								required: true,
								message: "Please input Employee ID!",
							},
						]}>
						<Input placeholder='OE-012' />
					</Form.Item>
					<Form.Item
						style={{ marginBottom: "10px" }}
						label='Blood Group'
						name='bloodGroup'
						rules={[
							{
								required: true,
								message: "Please input Blood Group!",
							},
						]}>
						<Select
							placeholder='Select Blood Group'
							allowClear
							defaultValue={initialValues.bloodGroup}
							mode='single'
							size='middle'
							style={{
								width: "100%",
							}}>
							{bloodGroups.map((bloodGroup) => (
								<Option key={bloodGroup} value={bloodGroup}>
									{bloodGroup}
								</Option>
							))}
						</Select>
					</Form.Item> */}
					{/* TODO: Add a Upload Seciton for Image */}

					{/* <Form.Item
						name={"departmentId"}
						style={{ marginBottom: "10px" }}
						label='Department'
						rules={[{ required: true, message: "Please input Department!" }]}>
						<Select
							onChange={(value) => setDepartmentId(value)}
							placeholder='Select Department'
							allowClear
							size={"middle"}
							defaultValue={initialValues.departmentId}>
							{department &&
								department.map((department) => (
									<Option key={department.id} value={department.id}>
										{department.name}
									</Option>
								))}
						</Select>
					</Form.Item>
					<Form.Item
						rules={[
							{
								required: true,
								message: "Pleases Select Type!",
							},
						]}
						label='Role'
						name={"roleId"}
						style={{ marginBottom: "10px" }}>
						<Select
							onChange={(value) => setRoleId(value)}
							defaultValue={initialValues.roleId}
							loading={!list}
							size='middle'
							mode='single'
							allowClear
							style={{
								width: "100%",
							}}
							placeholder='Please select Role'>
							{list &&
								list.map((role) => (
									<Option key={role.id} value={role.id}>
										{role.name}
									</Option>
								))}
						</Select>
					</Form.Item> */}

					{/* <Form.Item
						rules={[
							{
								required: true,
								message: "Pleases Select Type!",
							},
						]}
						label='Shift'
						name={"shiftId"}
						style={{ marginBottom: "10px" }}>
						<Select
							onChange={(value) => setShiftId(value)}
							defaultValue={initialValues.shiftId}
							loading={!shift}
							size='middle'
							mode='single'
							allowClear
							style={{
								width: "100%",
							}}
							placeholder='Please select shift'>
							{shift &&
								shift.map((shift) => (
									<Option key={shift.id} value={shift.id}>
										{shift.name}
									</Option>
								))}
						</Select>
					</Form.Item>

					<Form.Item
						rules={[
							{
								required: true,
								message: "Pleases Select Type!",
							},
						]}
						label='Leave Policy'
						name={"leavePolicyId"}
						style={{ marginBottom: "10px" }}>
						<Select
							onChange={(value) => setLeavePolicyId(value)}
							defaultValue={initialValues.leavePolicyId}
							loading={!leavePolicy}
							size='middle'
							mode='single'
							allowClear
							style={{
								width: "100%",
							}}
							placeholder='Please select leavePolicy'>
							{leavePolicy &&
								leavePolicy.map((leavePolicy) => (
									<Option key={leavePolicy.id} value={leavePolicy.id}>
										{leavePolicy.name}
									</Option>
								))}
						</Select>
					</Form.Item>

					<Form.Item
						rules={[
							{
								required: true,
								message: "Pleases Select Type!",
							},
						]}
						label='Weekly Holiday'
						name={"weeklyHolidayId"}
						style={{ marginBottom: "10px" }}>
						<Select
							onChange={(value) => setWeeklyHolidayId(value)}
							defaultValue={initialValues.weeklyHolidayId}
							loading={!weeklyHoliday}
							size='middle'
							mode='single'
							allowClear
							style={{
								width: "100%",
							}}
							placeholder='Please select weeklyHoliday'>
							{weeklyHoliday &&
								weeklyHoliday.map((weeklyHoliday) => (
									<Option key={weeklyHoliday.id} value={weeklyHoliday.id}>
										{weeklyHoliday.name}
									</Option>
								))}
						</Select>
					</Form.Item> */}

					<Form.Item
						style={{ marginBottom: "10px", marginTop: "10px" }}
						wrapperCol={{
							offset: 4,
							span: 16,
						}}>
						<Button
							className='mt-5'
							block
							onClick={() => setLoader(true)}
							type='primary'
							shape='round'
							htmlType='submit'
							loading={loader}>
							Update User
						</Button>
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title='Stripe Onboarding Invite Link'
				open={stripeInviteModalOpen}
				onCancel={() => setStripeInviteModalOpen(false)}
				footer={[
					<Button key='close' onClick={() => setStripeInviteModalOpen(false)}>
						Close
					</Button>,
				]}>
				<p>
					Share this link with the service man so they can complete their
					payout setup:
				</p>
				<Typography.Paragraph
					copyable={{ text: stripeInviteLink }}
					style={{
						background: "#fafafa",
						border: "1px solid #f0f0f0",
						borderRadius: 6,
						padding: "8px 12px",
						wordBreak: "break-all",
						marginBottom: 0,
					}}>
					{stripeInviteLink}
				</Typography.Paragraph>
			</Modal>
		</>
	);
};
export default ProfileEditPopup;
